import type { TFunction } from 'i18next';

/** GET /payment/campaign-bonus-offers row (Kiosk_Backend campaignBonusService.toPublicOffer). */
export type CampaignOffer = {
  id: string;
  playerMessage: string;
  application: 'first_deposit' | 'every_deposit';
  bonusType: 'fixed' | 'percentage';
  bonusValue: number;
  minDeposit: number;
  maxBonusPerDeposit: number;
  endsAt: string | null;
  /** True after this player has taken the one first-deposit slot. */
  used?: boolean;
};

export type CampaignOfferPayload = {
  offers: CampaignOffer[];
  offerKey: string;
  popupDismissed: boolean;
};

type RewardFields = Pick<CampaignOffer, 'bonusType' | 'bonusValue' | 'maxBonusPerDeposit'>;
type RuleFields = Pick<CampaignOffer, 'application' | 'minDeposit'>;

export function formatOfferMoney(amount: number) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '$0';
  return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
}

function plainNumber(amount: number) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return '0';
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}

/** The figure for a value of this offer's kind ("+$10", "50%"); count-ups pass the running value. */
export function formatRewardValue(offer: Pick<CampaignOffer, 'bonusType' | 'bonusValue'>, value = offer.bonusValue) {
  // While counting up, whole steps; the last frame shows the exact figure.
  const shown = value === offer.bonusValue ? value : Math.floor(value);
  return offer.bonusType === 'fixed' ? `+${formatOfferMoney(shown)}` : `${plainNumber(shown)}%`;
}

/** The ticket figure and its caption: "+$10" · "bonus", or "50%" · "up to $25". */
export function offerReward(offer: RewardFields, t: TFunction) {
  if (offer.bonusType === 'fixed') {
    return { value: formatRewardValue(offer), caption: t('campaignBonus.rewardFixed', 'bonus') };
  }
  return {
    value: formatRewardValue(offer),
    caption: t('campaignBonus.rewardUpTo', 'up to {{amount}}', { amount: formatOfferMoney(offer.maxBonusPerDeposit) }),
  };
}

/** The reward inside a sentence: "+$10 bonus", "50% bonus up to $25". */
export function offerRewardLine(offer: RewardFields, t: TFunction) {
  if (offer.bonusType === 'fixed') {
    return t('campaignBonus.rewardLineFixed', '+{{amount}} bonus', { amount: formatOfferMoney(offer.bonusValue) });
  }
  return t('campaignBonus.rewardLinePercent', '{{percent}}% bonus up to {{amount}}', {
    percent: plainNumber(offer.bonusValue),
    amount: formatOfferMoney(offer.maxBonusPerDeposit),
  });
}

/** Which deposits qualify: "First deposit · $10 or more". */
export function offerRule(offer: RuleFields, t: TFunction) {
  const min = formatOfferMoney(offer.minDeposit);
  return offer.application === 'first_deposit'
    ? t('campaignBonus.ruleFirst', 'First deposit · {{min}} or more', { min })
    : t('campaignBonus.ruleEvery', 'Every deposit · {{min}} or more', { min });
}

/** Figure size step, so "+$12.50" fits the ticket stub as well as "+$10" ("%" counts as two). */
export function rewardSize(value: string): 'lg' | 'md' | 'sm' {
  const width = value.length + (value.endsWith('%') ? 1 : 0);
  if (width <= 4) return 'lg';
  return width <= 5 ? 'md' : 'sm';
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
/** Under a day the countdown turns urgent; over a week it shows the date. */
const URGENT_MS = DAY;
const DATE_AFTER_MS = 7 * DAY;

export type OfferTimeLeft =
  | { state: 'none'; urgent: false }
  | { state: 'ended'; urgent: true }
  | { state: 'date'; end: Date; urgent: false }
  | { state: 'days'; days: number; hours: number; urgent: false }
  | { state: 'hours'; hours: number; minutes: number; urgent: true }
  | { state: 'minutes'; minutes: number; urgent: true };

export function offerTimeLeft(endsAt: string | null, now: number): OfferTimeLeft {
  const end = endsAt ? new Date(endsAt) : null;
  if (!end || Number.isNaN(end.getTime())) return { state: 'none', urgent: false };
  const left = end.getTime() - now;
  if (left <= 0) return { state: 'ended', urgent: true };
  if (left > DATE_AFTER_MS) return { state: 'date', end, urgent: false };
  if (left >= URGENT_MS) {
    return { state: 'days', days: Math.floor(left / DAY), hours: Math.floor((left % DAY) / HOUR), urgent: false };
  }
  if (left >= HOUR) {
    return { state: 'hours', hours: Math.floor(left / HOUR), minutes: Math.floor((left % HOUR) / MINUTE), urgent: true };
  }
  return { state: 'minutes', minutes: Math.max(1, Math.ceil(left / MINUTE)), urgent: true };
}

export function offerTimeLeftLabel(left: OfferTimeLeft, t: TFunction, locale = 'en-US'): string | null {
  switch (left.state) {
    case 'none':
      return null;
    case 'ended':
      return t('campaignBonus.ended', 'Ended');
    case 'date':
      return t('campaignBonus.until', 'Until {{date}}', {
        date: new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(left.end),
      });
    case 'days':
      return left.hours > 0
        ? t('campaignBonus.endsInDays', 'Ends in {{days}}d {{hours}}h', { days: left.days, hours: left.hours })
        : t('campaignBonus.endsInDaysOnly', 'Ends in {{days}}d', { days: left.days });
    case 'hours':
      return t('campaignBonus.endsInHours', 'Ends in {{hours}}h {{minutes}}m', { hours: left.hours, minutes: left.minutes });
    default:
      return t('campaignBonus.endsInMinutes', 'Ends in {{minutes}}m', { minutes: left.minutes });
  }
}

/** Offers the player can still claim; a used first-deposit slot stays listed as Claimed. */
export function openOffers(offers: CampaignOffer[]) {
  return offers.filter((offer) => !offer.used);
}

/** The cheapest open offer this amount does not reach yet (the meter's next goal). */
export function nextUnlock(offers: CampaignOffer[], amount: number, applied: Set<string>) {
  let best: CampaignOffer | null = null;
  for (const offer of openOffers(offers)) {
    if (applied.has(offer.id) || offer.minDeposit <= amount + 1e-9) continue;
    if (!best || offer.minDeposit < best.minDeposit) best = offer;
  }
  return best;
}
